export interface WizardState {
  buildingType: string
  chargeModel: string
  features: {
    trials: boolean
    coupons: boolean
    tax: boolean
    invoices: boolean
  }
  billingManager: string
  stack: { frontend: string; backend: string; database: string }
  stripeAccount: string
  stripeAccountId: string
  products: UserProduct[]
}

export interface UserProduct {
  name: string
  price: number
  currency: string
  billingType:
    | 'recurring_monthly'
    | 'recurring_annual'
    | 'recurring_both'
    | 'one_time'
}

export interface StripeModule {
  name: string
  description: string
  reason: string
  iconName: string
  optional?: boolean
}

export interface DataTable {
  name: string
  columns: string[]
  stripeRef: string
}

export interface StripeObject {
  type: 'product' | 'price' | 'webhook' | 'portal'
  name: string
  id: string
  details?: string
}

export interface Recommendation {
  modules: StripeModule[]
  events: string[]
  tables: DataTable[]
  rationale: string[]
  warnings: { title: string; description: string }[]
  frontendLabel: string
  backendLabel: string
  dbLabel: string
  stripeObjects: StripeObject[]
  provisioningSteps: { label: string; detail: string }[]
}

const DEFAULT_STATE: WizardState = {
  buildingType: 'SaaS App',
  chargeModel: 'Subscription',
  features: { trials: true, coupons: false, tax: false, invoices: true },
  billingManager: 'Customers self-serve',
  stack: { frontend: 'Next.js', backend: 'Node.js', database: 'PostgreSQL' },
  stripeAccount: 'No',
  stripeAccountId: '',
  products: [
    {
      name: 'Starter',
      price: 9,
      currency: 'USD',
      billingType: 'recurring_both',
    },
    { name: 'Pro', price: 29, currency: 'USD', billingType: 'recurring_both' },
    {
      name: 'Enterprise',
      price: 99,
      currency: 'USD',
      billingType: 'recurring_both',
    },
  ],
}

export function getWizardState(): WizardState {
  try {
    const saved = localStorage.getItem('paykit_wizard_state')
    if (saved) return JSON.parse(saved)
  } catch {}
  return DEFAULT_STATE
}

export function getRecommendation(state: WizardState): Recommendation {
  // --- Modules ---
  const modules: StripeModule[] = []

  const checkoutReason = ['Subscription', 'Freemium', 'Multiple'].includes(
    state.chargeModel,
  )
    ? 'Handles subscription signup with built-in plan selection'
    : state.chargeModel === 'Usage-based'
      ? 'Collects payment method for metered billing'
      : 'Fastest way to accept one-time payments'

  modules.push({
    name: 'Stripe Checkout',
    description: 'Hosted payment page optimized for conversion.',
    reason: checkoutReason,
    iconName: 'CreditCard',
  })

  if (
    ['Subscription', 'Freemium', 'Multiple', 'Usage-based'].includes(
      state.chargeModel,
    )
  ) {
    modules.push({
      name: 'Stripe Billing',
      description: 'Manage subscriptions, invoices, and recurring payments.',
      reason:
        state.chargeModel === 'Usage-based'
          ? 'Required for metered billing and usage record reporting'
          : 'Automates recurring charges and plan management',
      iconName: 'Repeat',
    })
  }

  if (
    state.billingManager === 'Customers self-serve' ||
    state.billingManager === 'Both'
  ) {
    modules.push({
      name: 'Customer Portal',
      description: 'Self-serve billing management for your users.',
      reason: 'Lets customers update payment methods, switch plans, and cancel',
      iconName: 'Users',
    })
  }

  modules.push({
    name: 'Webhooks',
    description: 'Real-time event notifications from Stripe.',
    reason: 'Essential for keeping your database in sync with Stripe',
    iconName: 'Webhook',
  })

  if (state.features.tax) {
    modules.push({
      name: 'Stripe Tax',
      description: 'Automatic sales tax, VAT, and GST calculation.',
      reason: 'Handles tax compliance across jurisdictions automatically',
      iconName: 'Calculator',
      optional: true,
    })
  }

  if (state.buildingType === 'Marketplace') {
    modules.push({
      name: 'Stripe Connect',
      description: 'Multi-party payments and seller onboarding.',
      reason: 'Required for routing payments between buyers and sellers',
      iconName: 'Link',
    })
  }

  // --- Events ---
  const events: string[] = ['checkout.session.completed']
  if (
    ['Subscription', 'Freemium', 'Multiple', 'Usage-based'].includes(
      state.chargeModel,
    )
  ) {
    events.push(
      'customer.subscription.created',
      'customer.subscription.updated',
      'customer.subscription.deleted',
    )
  }
  events.push('invoice.payment_succeeded', 'invoice.payment_failed')
  if (state.features.coupons) events.push('customer.discount.created')
  if (state.features.tax) events.push('tax.calculation.created')
  if (state.buildingType === 'Marketplace')
    events.push('account.updated', 'transfer.created')
  if (
    state.billingManager === 'Customers self-serve' ||
    state.billingManager === 'Both'
  ) {
    events.push('billing_portal.session.created')
  }

  // --- Tables ---
  const tables: DataTable[] = [
    {
      name: 'users',
      columns: ['id', 'email', 'name', 'stripe_customer_id', 'created_at'],
      stripeRef: 'cus_xxx',
    },
  ]
  if (
    ['Subscription', 'Freemium', 'Multiple', 'Usage-based'].includes(
      state.chargeModel,
    )
  ) {
    tables.push({
      name: 'subscriptions',
      columns: [
        'id',
        'user_id',
        'stripe_subscription_id',
        'status',
        'plan',
        'current_period_end',
        'created_at',
      ],
      stripeRef: 'sub_xxx',
    })
  }
  if (state.chargeModel === 'One-time' || state.chargeModel === 'Multiple') {
    tables.push({
      name: 'purchases',
      columns: [
        'id',
        'user_id',
        'stripe_payment_intent_id',
        'amount',
        'status',
        'created_at',
      ],
      stripeRef: 'pi_xxx',
    })
  }
  if (
    state.features.invoices ||
    ['Subscription', 'Freemium', 'Multiple'].includes(state.chargeModel)
  ) {
    tables.push({
      name: 'invoices',
      columns: [
        'id',
        'user_id',
        'stripe_invoice_id',
        'amount_due',
        'status',
        'created_at',
      ],
      stripeRef: 'in_xxx',
    })
  }
  tables.push({
    name: 'webhook_events',
    columns: ['id', 'stripe_event_id', 'type', 'processed', 'created_at'],
    stripeRef: 'evt_xxx',
  })

  // --- Stripe Objects ---
  const stripeObjects: StripeObject[] = []
  const currencySymbol = (c: string) => {
    if (c === 'USD') return '$'
    if (c === 'GBP') return '£'
    if (c === 'EUR') return '€'
    return c + ' '
  }

  if (state.products && state.products.length > 0) {
    state.products.forEach((p) => {
      const prodId = 'prod_PK' + randomId()
      stripeObjects.push({
        type: 'product',
        name: p.name,
        id: prodId,
        details: p.billingType.includes('recurring')
          ? 'Subscription'
          : 'One-time',
      })

      const sym = currencySymbol(p.currency)

      if (p.billingType === 'recurring_both') {
        stripeObjects.push({
          type: 'price',
          name: `${p.name} Monthly (${sym}${p.price})`,
          id: 'price_PK' + randomId(),
        })
        stripeObjects.push({
          type: 'price',
          name: `${p.name} Annual (${sym}${p.price * 10})`,
          id: 'price_PK' + randomId(),
        })
      } else if (p.billingType === 'recurring_monthly') {
        stripeObjects.push({
          type: 'price',
          name: `${p.name} Monthly (${sym}${p.price})`,
          id: 'price_PK' + randomId(),
        })
      } else if (p.billingType === 'recurring_annual') {
        stripeObjects.push({
          type: 'price',
          name: `${p.name} Annual (${sym}${p.price})`,
          id: 'price_PK' + randomId(),
        })
      } else {
        stripeObjects.push({
          type: 'price',
          name: `${p.name} (${sym}${p.price})`,
          id: 'price_PK' + randomId(),
        })
      }
    })
  } else {
    const isRecurring = ['Subscription', 'Freemium', 'Multiple'].includes(
      state.chargeModel,
    )

    if (isRecurring) {
      stripeObjects.push(
        {
          type: 'product',
          name: 'Starter Plan',
          id: 'prod_PK' + randomId(),
          details: 'Basic features',
        },
        {
          type: 'product',
          name: 'Pro Plan',
          id: 'prod_PK' + randomId(),
          details: 'Advanced features',
        },
        {
          type: 'product',
          name: 'Enterprise Plan',
          id: 'prod_PK' + randomId(),
          details: 'Custom features',
        },
        {
          type: 'price',
          name: 'Starter Monthly ($9)',
          id: 'price_PK' + randomId(),
        },
        {
          type: 'price',
          name: 'Starter Annual ($90)',
          id: 'price_PK' + randomId(),
        },
        {
          type: 'price',
          name: 'Pro Monthly ($29)',
          id: 'price_PK' + randomId(),
        },
        {
          type: 'price',
          name: 'Pro Annual ($290)',
          id: 'price_PK' + randomId(),
        },
        {
          type: 'price',
          name: 'Enterprise Monthly ($99)',
          id: 'price_PK' + randomId(),
        },
      )
    } else if (state.chargeModel === 'Usage-based') {
      stripeObjects.push(
        {
          type: 'product',
          name: 'API Access',
          id: 'prod_PK' + randomId(),
          details: 'Metered usage',
        },
        {
          type: 'price',
          name: 'Per-unit ($0.01/request)',
          id: 'price_PK' + randomId(),
        },
      )
    } else {
      stripeObjects.push(
        {
          type: 'product',
          name: 'Standard License',
          id: 'prod_PK' + randomId(),
          details: 'One-time purchase',
        },
        { type: 'price', name: 'Standard ($49)', id: 'price_PK' + randomId() },
        { type: 'price', name: 'Premium ($99)', id: 'price_PK' + randomId() },
      )
    }
  }

  stripeObjects.push({
    type: 'webhook',
    name: 'Webhook Endpoint',
    id: 'we_PK' + randomId(),
    details: `${events.length} events`,
  })

  if (modules.some((m) => m.name === 'Customer Portal')) {
    stripeObjects.push({
      type: 'portal',
      name: 'Customer Portal Config',
      id: 'bpc_PK' + randomId(),
    })
  }

  // --- Provisioning Steps ---
  const provisioningSteps: { label: string; detail: string }[] = [
    {
      label: 'Verify Stripe connection',
      detail: 'Authenticating with Stripe API',
    },
    {
      label: `Create products (${stripeObjects.filter((o) => o.type === 'product').length})`,
      detail: 'Setting up product catalog',
    },
    {
      label: `Create prices (${stripeObjects.filter((o) => o.type === 'price').length})`,
      detail: 'Configuring pricing tiers',
    },
  ]

  if (modules.some((m) => m.name === 'Customer Portal')) {
    provisioningSteps.push({
      label: 'Configure Customer Portal',
      detail: 'Setting up self-serve billing',
    })
  }

  if (state.features.tax) {
    provisioningSteps.push({
      label: 'Enable Stripe Tax',
      detail: 'Configuring automatic tax calculation',
    })
  }

  provisioningSteps.push(
    {
      label: `Create webhook endpoint (${events.length} events)`,
      detail: 'Subscribing to lifecycle events',
    },
    {
      label: 'Generate implementation pack',
      detail: 'Building code snippets and guides',
    },
  )

  // --- Rationale ---
  const rationale: string[] = []
  if (modules.some((m) => m.name === 'Stripe Checkout')) {
    rationale.push(
      "Stripe Checkout handles PCI compliance, 3D Secure, and payment method display so you don't have to build payment UI.",
    )
  }
  if (modules.some((m) => m.name === 'Customer Portal')) {
    rationale.push(
      'The Customer Portal eliminates the need to build plan management UI.',
    )
  }
  if (modules.some((m) => m.name === 'Stripe Billing')) {
    rationale.push(
      'Stripe Billing automates invoice generation, retry logic, and proration calculations.',
    )
  }
  rationale.push(
    `The selected ${events.length} webhook events cover the full lifecycle of your ${state.chargeModel?.toLowerCase() || 'payment'} model.`,
  )

  // --- Warnings ---
  const warnings: { title: string; description: string }[] = []
  if (state.chargeModel === 'Usage-based') {
    warnings.push({
      title: 'Usage reporting required',
      description:
        "You'll need a cron job or background worker to report usage to Stripe periodically.",
    })
  }
  if (state.buildingType === 'Marketplace') {
    warnings.push({
      title: 'Seller onboarding complexity',
      description:
        'Stripe Connect requires seller identity verification and onboarding flows.',
    })
  }
  if (state.features.tax) {
    warnings.push({
      title: 'Address collection needed',
      description:
        'Stripe Tax requires customer billing addresses to calculate the correct tax rate.',
    })
  }

  const frontendLabel = state.stack.frontend || 'React'
  const backendLabel = state.stack.backend || 'Node.js'
  const dbLabel = state.stack.database || 'PostgreSQL'

  return {
    modules,
    events,
    tables,
    rationale,
    warnings,
    frontendLabel,
    backendLabel,
    dbLabel,
    stripeObjects,
    provisioningSteps,
  }
}

function randomId(): string {
  return Math.random().toString(36).substring(2, 10).toUpperCase()
}

// --- Code generation helpers ---

export function generateEnvVars(
  state: WizardState,
  rec: Recommendation,
): string {
  const lines = [
    '# Stripe API Keys',
    `${state.stack.frontend === 'Next.js' ? 'NEXT_PUBLIC_' : ''}STRIPE_PUBLISHABLE_KEY=pk_test_51N...`,
    'STRIPE_SECRET_KEY=sk_test_51N...',
    '',
    '# Stripe Webhook Secret',
    'STRIPE_WEBHOOK_SECRET=whsec_...',
  ]

  if (state.features.tax) {
    lines.push('', '# Stripe Tax', 'STRIPE_TAX_ENABLED=true')
  }

  lines.push(
    '',
    '# App URL',
    `${state.stack.frontend === 'Next.js' ? 'NEXT_PUBLIC_' : ''}APP_URL=http://localhost:3000`,
  )

  if (state.stack.database === 'Supabase') {
    lines.push(
      '',
      '# Supabase',
      'SUPABASE_URL=https://your-project.supabase.co',
      'SUPABASE_ANON_KEY=eyJ...',
    )
  } else if (state.stack.database === 'PlanetScale') {
    lines.push('', '# PlanetScale', 'DATABASE_URL=mysql://...')
  } else if (state.stack.database === 'Firebase') {
    lines.push('', '# Firebase', 'FIREBASE_PROJECT_ID=your-project')
  } else {
    lines.push(
      '',
      '# Database',
      `DATABASE_URL=${state.stack.database === 'MongoDB' ? 'mongodb://localhost:27017/app' : 'postgresql://localhost:5432/app'}`,
    )
  }

  return lines.join('\n')
}

export function generateWebhookHandler(
  state: WizardState,
  rec: Recommendation,
): string {
  if (state.stack.backend === 'Python') {
    return `from flask import Flask, request, jsonify
import stripe

app = Flask(__name__)
stripe.api_key = os.environ["STRIPE_SECRET_KEY"]
endpoint_secret = os.environ["STRIPE_WEBHOOK_SECRET"]

@app.route("/webhook", methods=["POST"])
def webhook():
    payload = request.get_data()
    sig_header = request.headers.get("Stripe-Signature")

    try:
        event = stripe.Webhook.construct_event(payload, sig_header, endpoint_secret)
    except ValueError:
        return "Invalid payload", 400
    except stripe.error.SignatureVerificationError:
        return "Invalid signature", 400

${rec.events.map((e) => `    if event["type"] == "${e}":\n        handle_${e.replace(/\./g, '_')}(event["data"]["object"])`).join('\n')}

    return jsonify(success=True)`
  }

  if (state.stack.frontend === 'Next.js') {
    return `import { NextRequest, NextResponse } from 'next/server'
import Stripe from 'stripe'

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!)
const webhookSecret = process.env.STRIPE_WEBHOOK_SECRET!

export async function POST(req: NextRequest) {
  const body = await req.text()
  const sig = req.headers.get('stripe-signature')!

  let event: Stripe.Event
  try {
    event = stripe.webhooks.constructEvent(body, sig, webhookSecret)
  } catch (err) {
    return NextResponse.json({ error: 'Invalid signature' }, { status: 400 })
  }

  switch (event.type) {
${rec.events.map((e) => `    case '${e}':\n      // Handle ${e}\n      break`).join('\n')}
  }

  return NextResponse.json({ received: true })
}`
  }

  // Default Node.js / Express
  return `import express from 'express'
import Stripe from 'stripe'

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!)
const webhookSecret = process.env.STRIPE_WEBHOOK_SECRET!

app.post('/webhook', express.raw({ type: 'application/json' }), (req, res) => {
  const sig = req.headers['stripe-signature']!

  let event: Stripe.Event
  try {
    event = stripe.webhooks.constructEvent(req.body, sig, webhookSecret)
  } catch (err) {
    return res.status(400).send('Webhook signature verification failed')
  }

  switch (event.type) {
${rec.events.map((e) => `    case '${e}':\n      // Handle ${e}\n      break`).join('\n')}
  }

  res.json({ received: true })
})`
}

export function generateDbSchema(
  state: WizardState,
  rec: Recommendation,
): string {
  const isSql = !['MongoDB', 'Firebase'].includes(state.stack.database)

  if (!isSql) {
    return `// ${state.stack.database} Collections\n\n${rec.tables
      .map(
        (t) =>
          `// Collection: ${t.name}\n// Fields: ${t.columns.join(', ')}\n// Stripe ref: ${t.stripeRef}`,
      )
      .join('\n\n')}`
  }

  return rec.tables
    .map((t) => {
      const cols = t.columns.map((c) => {
        if (c === 'id') return '  id UUID PRIMARY KEY DEFAULT gen_random_uuid()'
        if (c === 'created_at') return '  created_at TIMESTAMPTZ DEFAULT now()'
        if (c === 'user_id') return '  user_id UUID REFERENCES users(id)'
        if (c === 'status') return "  status TEXT NOT NULL DEFAULT 'active'"
        if (c === 'processed') return '  processed BOOLEAN DEFAULT false'
        if (c.includes('amount')) return `  ${c} INTEGER NOT NULL`
        return `  ${c} TEXT`
      })
      return `CREATE TABLE ${t.name} (\n${cols.join(',\n')}\n);`
    })
    .join('\n\n')
}

export function generateBackendEndpoints(
  state: WizardState,
  rec: Recommendation,
): { method: string; path: string; description: string; code: string }[] {
  const isNextJs = state.stack.frontend === 'Next.js'
  const endpoints: {
    method: string
    path: string
    description: string
    code: string
  }[] = []

  endpoints.push({
    method: 'POST',
    path: isNextJs ? '/api/checkout' : '/checkout',
    description: 'Create a Stripe Checkout session',
    code: isNextJs
      ? `export async function POST(req: Request) {
  const { priceId } = await req.json()
  const session = await stripe.checkout.sessions.create({
    mode: '${['Subscription', 'Freemium', 'Multiple'].includes(state.chargeModel) ? 'subscription' : 'payment'}',
    line_items: [{ price: priceId, quantity: 1 }],
    success_url: \`\${process.env.NEXT_PUBLIC_APP_URL}/success\`,
    cancel_url: \`\${process.env.NEXT_PUBLIC_APP_URL}/pricing\`,
  })
  return Response.json({ url: session.url })
}`
      : `app.post('/checkout', async (req, res) => {
  const { priceId } = req.body
  const session = await stripe.checkout.sessions.create({
    mode: '${['Subscription', 'Freemium', 'Multiple'].includes(state.chargeModel) ? 'subscription' : 'payment'}',
    line_items: [{ price: priceId, quantity: 1 }],
    success_url: \`\${process.env.APP_URL}/success\`,
    cancel_url: \`\${process.env.APP_URL}/pricing\`,
  })
  res.json({ url: session.url })
})`,
  })

  if (rec.modules.some((m) => m.name === 'Customer Portal')) {
    endpoints.push({
      method: 'POST',
      path: isNextJs ? '/api/portal' : '/portal',
      description: 'Create a Customer Portal session',
      code: isNextJs
        ? `export async function POST(req: Request) {
  const { customerId } = await req.json()
  const session = await stripe.billingPortal.sessions.create({
    customer: customerId,
    return_url: process.env.NEXT_PUBLIC_APP_URL + '/dashboard',
  })
  return Response.json({ url: session.url })
}`
        : `app.post('/portal', async (req, res) => {
  const { customerId } = req.body
  const session = await stripe.billingPortal.sessions.create({
    customer: customerId,
    return_url: process.env.APP_URL + '/dashboard',
  })
  res.json({ url: session.url })
})`,
    })
  }

  endpoints.push({
    method: 'POST',
    path: isNextJs ? '/api/webhook' : '/webhook',
    description: 'Handle Stripe webhook events',
    code: '// See Webhooks tab for full implementation',
  })

  return endpoints
}
